// ui/discard-prompt-position.js: BATCH B1 round 5, V2 + fix5 (W1, W2).
//
// V2: in the short-landscape layouts (667x375, 844x390, 640x320, 568x320)
// the DiscardPrompt rendered below the canvas (in flow: y 387 on 667x375,
// y 402 on 844x390), outside the viewport, so the dealer could not see
// the Discard button without scrolling. Like the portrait prompt it now
// overlays the table: absolute inside the stage, its bottom a small gap
// above the hand fan, clear of the hand cards, the You plate and the
// timer ring. DiscardPrompt.jsx positions itself from this rect; the unit
// test pins the invariants.
//
// W2 (fix5): that overlay was full-width, so it covered the up-card's
// face (at 667x375 the up-card is y 189-279 in page coords and the prompt
// y 211-279). When the full-width band above the hand hits the up-card,
// the prompt moves beside the up-card (right first, then left), keeping
// the same bottom edge above the hand, so it stays disjoint from the
// up-card too.
//
// W1 (fix5): desktop keeps the in-flow prompt below the canvas, but on
// short windows (800x600: the Discard button at y 566-606, 6px below the
// screen; 1024x600: the whole prompt at y 612-680) it cannot be reached.
// When the canvas bottom plus the prompt height does not fit the
// viewport, desktop overlays the prompt inside the stage the same way:
// above the hand, clear of the hand cards, the You plate, the timer ring
// and the up-card. Otherwise it stays in flow (null).
//
// fix6 (Q1, Q2b, Q3): see the constants and computeDiscardPromptRect.

import { trumpBadgeStackRect } from '../canvas/trump-badge.js';
import { timerRingRectForPlate } from '../canvas/layout.js';

// Gap between the prompt's bottom edge and the top of the hand fan.
export const DISCARD_PROMPT_GAP = 6;
// Prompt box height for the overlay rect. B3c r2: this is the real
// tallest box, not a guess. Before a card is picked the box is 72px
// (2 x 12px padding, the 44px phone Discard button, 2 x 2px border).
// Once a card is picked, the landscape overlay (at most 300px wide)
// wraps "You picked Ten of Diamonds. Click Discard to confirm." to
// three 16px lines, and the box grows to 2 x 12 + 48 + 4 = 76px.
export const DISCARD_PROMPT_H = 76;
// B3c r2: in the short-landscape overlay the prompt sits this much
// higher than its 6px gap needs, so the 76px box keeps 10px of room
// above the hand at every size (it was only applied up to 552px wide).
export const DISCARD_PROMPT_SLACK = 4;
// B3c r2: in an overlay narrower than this (the slot beside the up-card
// at 460x320 to 480x320 is 200-216px) the full picked-card label wraps to
// four 16px lines beside the Discard button and the box grows to 92px
// (measured in Chromium; at 238px wide it is three lines, 76px), past
// its rect and onto the hand. DiscardPrompt.jsx drops the label's
// "Click Discard to confirm." there (the Discard button sits next to it),
// so the box keeps to three lines and the 76px rect.
export const DISCARD_PROMPT_SHORT_LABEL_W = 230;
// fix7 N3: the portrait prompt's real rendered height (12px padding +
// the 40px button row + 2 x 2px border = 68px). The portrait rect used
// the 76px conservative height, so at every portrait size it ran 6px
// onto the timer ring (375x667: prompt y 309-385, ring y 379-415),
// while the rendered box ends 2px above the ring.
export const DISCARD_PORTRAIT_H = 68;
// fix6 Q3: the in-flow stack below the canvas is a 12px margin plus the
// 68px box (the 40px button row + 2 x 12px padding + 2 x 2px border).
// The fit check used 76px, so at viewport heights
// 796-799 the box border was clipped by up to 4px.
export const DISCARD_FLOW_H = 12 + 68;
// Viewport chrome above the canvas the layout reserves (layout.js sizes
// the desktop canvas as viewportH - 80): the header and its gap. The
// in-flow prompt fits only when canvas + chrome + prompt <= viewport.
export const DISCARD_CHROME_H = 80;
// fix6 Q1: the prompt's natural width (the label, the gap and the
// Discard button on one row). Every overlay slot is at most this wide;
// the slot right of the up-card used to run to the canvas edge and
// cover the right opponent's plate and fan.
export const DISCARD_PROMPT_W = 300;
// Minimum usable width for a prompt parked beside the up-card (a label
// plus the Discard button).
export const DISCARD_MIN_SIDE_W = 200;
// fix6 Q2: on the short landscapes the trump splash is on screen with
// the prompt (the bot orders up, the prompt mounts, the splash holds
// for 800ms) and the only free space left for it is the strip right of
// the prompt. Keep that strip this wide (an 8px gap, the 98px splash at
// its 60% type floor, an 8px edge) so 568x320 has room for it.
export const DISCARD_SPLASH_ROOM = 114;
// Portrait: the prompt's bottom edge sits this far above the timer ring
// (the 68px box then starts 88px above the You plate).
const PORTRAIT_RING_GAP = 2;

function rectsOverlap(a, b) {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * fix6 Q1: every table object the overlay prompt must not cover: the
 * four plates, the hand and the three opponent fans, the up-card, the
 * timer ring, the scoreboard and the trump badge with its label. Phones
 * pin the badge in the layout; desktop parks it beside the maker's
 * plate, so every seat's badge spot counts.
 *
 * @param {import('../canvas/layout.js').TableLayout} layout
 * @returns {Array<{x:number,y:number,w:number,h:number}>}
 */
export function discardPromptObstacles(layout) {
  const badges = layout.trumpBadge
    ? [trumpBadgeStackRect(layout, {})]
    : [1, 2, 3, 4].map((seat) => trumpBadgeStackRect(layout, { maker_seat: seat }));
  return [
    ...layout.seats,
    ...layout.seats.map((s) => s.handArea),
    layout.upCard,
    layout.scoreboard,
    ...(layout.timerRing ? [layout.timerRing] : []),
    .../** @type {Array<{x:number,y:number,w:number,h:number}>} */ (badges.filter(Boolean)),
  ];
}

/**
 * Rect for the dealer-discard prompt, in canvas coordinates, or null
 * when the prompt stays in flow below the canvas.
 *
 * - Portrait (fix6 Q2b): the overlay across the table, ending 2px above
 *   the timer ring. DiscardPrompt.jsx used to place it itself, so the
 *   trump splash never knew where it was. Callers that pass no viewport
 *   height keep the old answer (null). B3c r3: the rect is the
 *   prompt's measured height, growing upward (at 320x568 a picked card
 *   wraps the label to three lines and the box is 81px, not 68px).
 * - Short landscape: always an overlay.
 * - Desktop (and the rare non-compact mobile landscape): an overlay only
 *   when the in-flow prompt would not fit the viewport (null viewportH
 *   keeps the old behavior: in flow).
 *
 * An overlay is at most DISCARD_PROMPT_W wide and clear of every plate,
 * fan, the hand, the up-card, the timer ring, the scoreboard and the
 * trump badge. Slots, in order: just above the hand beside the up-card
 * (right, then left), centred above the hand, level with the up-card
 * (right, then left), then the trick area's empty band above the
 * up-card (from the left edge, then from the right edge). Short
 * landscapes too small for any of those (480x320) fall back to the
 * first slot that only covers face-down opponent fans. Null when no
 * slot is free (no supported size; the unit test pins that).
 *
 * B3c r3: measuredH is the rendered prompt's height (DiscardPrompt.jsx
 * reports it). Portrait uses it as is; the other overlays keep at least
 * promptH, so a box that renders taller still gets its full height.
 *
 * @param {import('../canvas/layout.js').TableLayout} layout
 * @param {number} [promptH]
 * @param {number|null} [viewportH]
 * @param {number|null} [measuredH]
 * @returns {{x:number, y:number, w:number, h:number}|null}
 */
export function computeDiscardPromptRect(layout, promptH = DISCARD_PROMPT_H, viewportH = null, measuredH = null) {
  if (layout.portrait) {
    if (viewportH == null) return null;
    // fix7 N3: the real 68px box until the prompt is measured, so the
    // rect ends 2px above the timer ring instead of overlapping it.
    const h = measuredH ?? Math.min(promptH, DISCARD_PORTRAIT_H);
    // The portrait ring is always the You plate's corner box.
    const ring = timerRingRectForPlate(layout.seats[0]);
    return { x: 6, y: ring.y - PORTRAIT_RING_GAP - h, w: layout.canvasW - 12, h };
  }
  if (measuredH != null) promptH = Math.max(promptH, measuredH);
  if (!layout.compact) {
    // Desktop / mobile in-flow prompt: it fits only when the canvas,
    // the chrome above it and the prompt stack inside the viewport.
    if (viewportH == null) return null;
    if (layout.canvasH + DISCARD_CHROME_H + DISCARD_FLOW_H <= viewportH) return null;
  }
  const hand = layout.seats[0].handArea;
  const up = layout.upCard;
  const obstacles = discardPromptObstacles(layout);
  const w = Math.min(DISCARD_PROMPT_W, layout.canvasW - 12);
  const inside = (r) => r.x >= 0 && r.y >= 0
    && r.x + r.w <= layout.canvasW && r.y + r.h <= layout.canvasH;
  const clears = (r) => inside(r) && obstacles.every((o) => !rectsOverlap(r, o));

  const handY = hand.y - DISCARD_PROMPT_GAP - promptH
    - (layout.compact ? DISCARD_PROMPT_SLACK : 0);
  const sideY = Math.max(6, Math.min(handY, up.y + Math.round((up.h - promptH) / 2)));
  const rightX = up.x + up.w + 8;
  const leftX = up.x - 8 - w;
  const rightW = layout.compact
    ? Math.max(DISCARD_MIN_SIDE_W, Math.min(w, layout.canvasW - DISCARD_SPLASH_ROOM - rightX))
    : w;
  const slots = [
    { x: rightX, y: handY, w: rightW },
    { x: leftX, y: handY, w },
    { x: hand.x + Math.round((hand.w - w) / 2), y: handY, w },
    { x: rightX, y: sideY, w: rightW },
    { x: leftX, y: sideY, w },
  ];
  for (const slot of slots) {
    const r = { ...slot, h: promptH };
    if (clears(r)) return r;
  }

  // The trick area's empty band above the up-card: from the left edge
  // up to the first object in that row (at 800x600 the partner fan at
  // x 286), then the same from the right edge.
  const bandY = up.y - DISCARD_PROMPT_GAP - promptH;
  const row = obstacles.filter((o) => o.y < bandY + promptH && bandY < o.y + o.h);
  const fromLeftEnd = Math.min(layout.canvasW - 6,
    ...row.filter((o) => o.x + o.w > 6).map((o) => o.x - 6));
  const fromLeft = { x: 6, y: bandY, w: Math.min(w, fromLeftEnd - 6), h: promptH };
  if (fromLeft.w >= DISCARD_MIN_SIDE_W && clears(fromLeft)) return fromLeft;
  const fromRightStart = Math.max(6,
    ...row.filter((o) => o.x < layout.canvasW - 6).map((o) => o.x + o.w + 6));
  const bandRightW = Math.min(w, layout.canvasW - 6 - fromRightStart);
  const fromRight = { x: layout.canvasW - 6 - bandRightW, y: bandY, w: bandRightW, h: promptH };
  if (fromRight.w >= DISCARD_MIN_SIDE_W && clears(fromRight)) return fromRight;
  // Last resort for the short-landscape layouts below the supported
  // sizes (480x320: 66px between the opponent fans and the hand): an
  // in-flow prompt there lands off screen, so park it above the hand
  // over the face-down opponent fans, still clear of everything else.
  if (layout.compact) {
    /** @type {Set<object>} */
    const fans = new Set(layout.seats.slice(1).map((s) => s.handArea));
    const rest = obstacles.filter((o) => !fans.has(o));
    for (const slot of slots) {
      const r = { ...slot, h: promptH };
      if (inside(r) && rest.every((o) => !rectsOverlap(r, o))) return r;
    }
  }
  return null;
}
